import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import dayjs from "dayjs";
import {
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Burger,
  Button,
  Group,
  Loader,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useHotkeys } from "@mantine/hooks";
import {
  AlertCircle,
  BarChart3,
  Bell,
  Calendar,
  CalendarOff,
  Command,
  History as HistoryIcon,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Percent,
  Search,
  type LucideIcon,
} from "lucide-react";
import {
  fetchMe,
  fetchWorkflowConfig,
  releaseHijack,
  type Me,
  type WorkflowConfig as WorkflowConfigType,
} from "./api/client";
import {
  approvalBadgeCount,
  timesheetBadgeCount,
  absenceBadgeCount,
  approvalTimesheetTabBadge,
  approvalAbsenceTabBadge,
} from "./hooks/useNotificationBadges";
import { useBadges } from "./hooks/BadgesContext";
import type { NotificationBadges } from "./api/client";
import { ACCENTS, NEUTRALS } from "./theme/tokens";
import { LoginScreen } from "./components/LoginScreen";
import { UserMenu } from "./components/UserMenu";
import { CommandPalette, type PaletteSection } from "./components/CommandPalette";
import { TimesheetGrid } from "./components/TimesheetGrid";
import { AbsenceList } from "./components/AbsenceList";
import { AbsenceApprovalQueue } from "./components/AbsenceApprovalQueue";
import { TimesheetApprovalQueue } from "./components/TimesheetApprovalQueue";
import { ManagerDashboard } from "./components/ManagerDashboard";
import { TeamAbsencesView } from "./components/TeamAbsencesView";
import { HistoryView } from "./components/HistoryView";
import { ExceptionsReportView } from "./components/ExceptionsReportView";
import { MonthlySummaryView } from "./components/MonthlySummaryView";
import { WeeklySummaryView } from "./components/WeeklySummaryView";
import { ZupExportView } from "./components/ZupExportView";
import { ReserveReportView } from "./components/ReserveReportView";
import { RatesView } from "./components/RatesView";

// ---------------------------------------------------------------------------
// Видимость разделов — в `access.ts`, чтобы тесты на предикаты не тащили
// за собой весь граф приложения (см. комментарий в том файле).
// ---------------------------------------------------------------------------

import {
  canApproveAbsences,
  canApproveAbsencesAsLRorHR,
  canApproveTimesheetsAsLR,
  canApproveTimesheetsAsPM,
  canExportZup,
  canManageRates,
  canSeeApprovals,
  canSeeDashboard,
  canSeeReports,
  canSeeReserveReport,
  canSeeSummaryReports,
} from "./access";

// ---------------------------------------------------------------------------
// Sidebar nav config
// ---------------------------------------------------------------------------

type PageId = "dashboard" | "timesheet" | "absence" | "approval" | "history" | "reports" | "rates";

interface NavItem {
  id: PageId;
  label: string;
  icon: LucideIcon;
  visible: (me: Me) => boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: "dashboard", label: "Сводка", icon: LayoutDashboard, visible: canSeeDashboard },
  // US-103.3: внешний согласующий (timesheet_exempt) таймшит не ведёт — скрываем.
  { id: "timesheet", label: "Таймшит", icon: Calendar, visible: (me) => !me.timesheet_exempt },
  { id: "absence", label: "Отсутствия", icon: CalendarOff, visible: () => true },
  { id: "approval", label: "На согласовании", icon: ListChecks, visible: canSeeApprovals },
  { id: "history", label: "История", icon: HistoryIcon, visible: () => true },
  { id: "reports", label: "Отчёты", icon: BarChart3, visible: canSeeReports },
  // US-113: история ставок — роль «Ведение ставок» + admin/staff.
  { id: "rates", label: "Ставки", icon: Percent, visible: canManageRates },
];

const PAGE_TITLES: Record<PageId, string> = {
  dashboard: "Сводка",
  timesheet: "Таймшит",
  absence: "Отсутствия",
  approval: "На согласовании",
  history: "История",
  reports: "Отчёты",
  rates: "Ставки",
};

// US-90: соответствие PageId ↔ URL-путь (basename="/app" задан в роутере).
const PAGE_PATHS: Record<PageId, string> = {
  dashboard: "/dashboard",
  timesheet: "/timesheet",
  absence: "/absences",
  approval: "/approvals",
  history: "/history",
  reports: "/reports",
  rates: "/rates",
};

/** Первый сегмент pathname → PageId. Неизвестный путь → "timesheet". */
function pageFromPathname(pathname: string): PageId {
  const seg = pathname.split("/").filter(Boolean)[0] ?? "";
  const found = (Object.keys(PAGE_PATHS) as PageId[]).find((id) => PAGE_PATHS[id] === `/${seg}`);
  return found ?? "timesheet";
}

/** Валидный YYYY-MM-DD → dayjs, иначе null (битый deep-link → текущая неделя). */
function parseWeekParam(value: string | undefined): dayjs.Dayjs | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = dayjs(value);
  return d.isValid() ? d.startOf("day") : null;
}

// ---------------------------------------------------------------------------
// Impersonation banner — full-width amber strip above the shell
// ---------------------------------------------------------------------------

interface ImpersonationBannerProps {
  username: string;
}

function ImpersonationBanner({ username }: ImpersonationBannerProps) {
  return (
    <div
      role="alert"
      style={{
        height: 36,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        background: "#3a2820",
        color: "#ffd5b8",
        fontSize: 12,
        fontWeight: 500,
        borderBottom: "1px solid #4d3328",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <AlertCircle size={14} />
        Вы работаете от имени <b style={{ color: "#fff" }}>{username}</b>. Действия пишутся в
        аудит-лог как impersonated.
      </span>
      <Button
        onClick={releaseHijack}
        variant="default"
        size="compact-xs"
        leftSection={<LogOut size={12} />}
        styles={{
          root: {
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "#fff",
          },
        }}
      >
        Выйти из сессии
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — logo, nav, account block
// ---------------------------------------------------------------------------

interface SidebarProps {
  me: Me;
  page: PageId;
  onPage: (p: PageId) => void;
  // US-44: счётчики бейджей для отображения у пунктов меню.
  badges: NotificationBadges | null;
}

function navBadgeCount(id: PageId, badges: NotificationBadges | null): number {
  switch (id) {
    case "timesheet":
      return timesheetBadgeCount(badges);
    case "absence":
      return absenceBadgeCount(badges);
    case "approval":
      return approvalBadgeCount(badges);
    default:
      return 0;
  }
}

function Sidebar({ me, page, onPage, badges }: SidebarProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";
  const neutrals =
    theme.other?.neutrals?.[dark ? "dark" : "light"] ?? NEUTRALS[dark ? "dark" : "light"];
  // Fallback when MantineProvider doesn't carry our theme (e.g. tests).
  const accentPrimary = theme.colors.brand?.[5] ?? ACCENTS.indigo[500];
  const items = NAV_ITEMS.filter((n) => n.visible(me));
  const initials = me.username.slice(0, 2).toUpperCase();
  const rolesLine = useMemo(() => {
    const roles: string[] = [];
    if (me.is_superuser) roles.push("admin");
    if (me.is_line_manager || me.roles?.includes("line_manager")) roles.push("lr");
    if (me.is_project_manager) roles.push("pm");
    if (me.is_hr || me.roles?.includes("hr")) roles.push("hr");
    if (me.is_pmo || me.roles?.includes("project_office_head")) roles.push("pmo");
    if (roles.length === 0) roles.push("employee");
    return roles.join(" · ");
  }, [me]);

  return (
    <Stack gap={0} h="100%" style={{ background: neutrals.surface, padding: "14px 10px" }}>
      <Group gap={8} px={8} pb="md">
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 5,
            background: accentPrimary,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--mantine-font-family-monospace)",
            fontWeight: 700,
            fontSize: 12,
          }}
          aria-hidden="true"
        >
          S
        </div>
        <Text fw={600} size="sm" style={{ letterSpacing: "-0.01em" }}>
          SKEDD
        </Text>
      </Group>

      <Stack gap={1}>
        {items.map((item) => {
          const Icon = item.icon;
          const active = page === item.id;
          const count = navBadgeCount(item.id, badges);
          return (
            <UnstyledButton
              key={item.id}
              onClick={() => onPage(item.id)}
              data-testid={`nav-${item.id}`}
              data-active={active || undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "7px 10px",
                background: active
                  ? dark
                    ? "rgba(255,255,255,0.06)"
                    : neutrals.surfaceAlt
                  : "transparent",
                color: active ? neutrals.text : neutrals.textMuted,
                fontWeight: active ? 500 : 400,
                borderRadius: 5,
                fontSize: 13,
                textAlign: "left",
                position: "relative",
              }}
            >
              <Icon size={15} color={active ? accentPrimary : "currentColor"} strokeWidth={1.75} />
              <span style={{ flex: 1 }}>{item.label}</span>
              {/* US-44: бейдж требующих действия. Скрыт когда count == 0. */}
              {count > 0 && (
                <Badge
                  size="xs"
                  color="red"
                  variant="filled"
                  radius="xl"
                  data-testid={`nav-${item.id}-badge`}
                  styles={{
                    root: {
                      minWidth: 18,
                      height: 16,
                      padding: "0 5px",
                      fontSize: 10,
                      fontWeight: 600,
                    },
                  }}
                >
                  {count}
                </Badge>
              )}
            </UnstyledButton>
          );
        })}
      </Stack>

      <Box style={{ flex: 1 }} />

      {/* US-100: статичная плашка идентичности. Дублирующее нижнее меню
          (тема + выход) убрано — единственное меню пользователя теперь в
          хедере (UserMenu), туда же переехал переключатель темы. */}
      <Group
        gap={9}
        wrap="nowrap"
        data-testid="sidebar-identity"
        style={{
          padding: "10px 10px",
          borderTop: `1px solid ${neutrals.border}`,
          marginTop: 8,
        }}
      >
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 999,
            background: neutrals.surfaceAlt,
            border: `1px solid ${neutrals.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: 11,
            color: neutrals.text,
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: neutrals.text,
            }}
          >
            {me.username}
          </div>
          <div
            style={{
              fontSize: 10.5,
              color: neutrals.textDim,
              fontFamily: "var(--mantine-font-family-monospace)",
            }}
          >
            {rolesLine}
          </div>
        </div>
      </Group>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Topbar — page title + delegation pill + actions
// ---------------------------------------------------------------------------

interface TopbarProps {
  pageTitle: string;
  me: Me;
  onOpenPalette: () => void;
  /** Мобильный бургер (рендерится слева от заголовка; на desktop скрыт). */
  burger?: React.ReactNode;
}

function Topbar({ pageTitle, me, onOpenPalette, burger }: TopbarProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";
  const neutrals =
    theme.other?.neutrals?.[dark ? "dark" : "light"] ?? NEUTRALS[dark ? "dark" : "light"];

  return (
    <Group
      h={52}
      px="md"
      gap="md"
      justify="space-between"
      style={{ background: neutrals.surface, borderBottom: `1px solid ${neutrals.border}` }}
    >
      <Group gap="xs" wrap="nowrap">
        {burger}
        <Title order={2} size="h4" style={{ letterSpacing: "-0.01em" }}>
          {pageTitle}
        </Title>
      </Group>
      <Group gap={6}>
        <Tooltip label="Поиск · ⌘K">
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Поиск"
            onClick={onOpenPalette}
            data-testid="topbar-search"
          >
            <Search size={15} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Уведомления (скоро)">
          <ActionIcon variant="subtle" color="gray" disabled aria-label="Уведомления">
            <Bell size={15} />
          </ActionIcon>
        </Tooltip>
        <UnstyledButton
          onClick={onOpenPalette}
          data-testid="topbar-cmdk"
          aria-label="Открыть командную палитру"
          visibleFrom="sm"
        >
          <Group
            gap={6}
            px={8}
            py={4}
            style={{
              border: `1px solid ${neutrals.border}`,
              borderRadius: 5,
              color: neutrals.textDim,
              fontFamily: "var(--mantine-font-family-monospace)",
              fontSize: 11,
            }}
          >
            <Command size={11} />K
          </Group>
        </UnstyledButton>
        <UserMenu me={me} />
      </Group>
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Page content — react-router routes (US-90)
// ---------------------------------------------------------------------------

// Legacy-редиректы для старых email-ссылок: /app/?week=YYYY-MM-DD и
// /app/?page=approval|absence. Письма, отправленные до US-90, продолжают
// открывать нужный раздел.
function LegacyRedirect({ me }: { me: Me }) {
  const [searchParams] = useSearchParams();
  const week = parseWeekParam(searchParams.get("week") ?? undefined);
  if (week) {
    return <Navigate to={`/timesheet/${week.format("YYYY-MM-DD")}`} replace />;
  }
  const page = searchParams.get("page");
  if (page === "approval") return <Navigate to="/approvals" replace />;
  if (page === "absence") return <Navigate to="/absences" replace />;
  // US-103.3: тот, кто не ведёт таймшит, приземляется на «На согласовании» —
  // для внешнего согласующего это его единственная функция.
  //
  // ...но только если этот раздел ему вообще доступен. Флаг задумывался под
  // внешнего кадровика, у которого согласование есть всегда. Если же флаг
  // поставить человеку без согласующих ролей (бухгалтер, юрист —
  // «непроизводственный персонал»), получалась петля: вход отправлял на
  // «На согласовании», роут без прав возвращал на «Таймшит», а «Таймшит» под
  // флагом снова отправлял на «На согласовании». Человек видел зависший экран
  // и в систему не попадал.
  if (me.timesheet_exempt) {
    return <Navigate to={canSeeApprovals(me) ? "/approvals" : "/absences"} replace />;
  }
  // US-93: руководитель по умолчанию приземляется на «Сводку», остальные — на таймшит.
  return <Navigate to={canSeeDashboard(me) ? "/dashboard" : "/timesheet"} replace />;
}

// /timesheet/:weekStart? — неделя из URL; смена недели в WeekPicker двигает URL.
function TimesheetPage() {
  const { weekStart } = useParams();
  const navigate = useNavigate();
  const parsed = useMemo(() => parseWeekParam(weekStart), [weekStart]);
  const handleWeekChange = useCallback(
    (next: dayjs.Dayjs) => {
      navigate(`/timesheet/${next.format("YYYY-MM-DD")}`);
    },
    [navigate],
  );
  return <TimesheetGrid weekStart={parsed ?? undefined} onWeekChange={handleWeekChange} />;
}

interface ApprovalsPageProps {
  me: Me;
  // US-44: счётчики для бейджей на суб-табах «На согласовании».
  badges: NotificationBadges | null;
  // US-50: текущая конфигурация workflow. null до загрузки или при ошибке.
  workflowConfig: WorkflowConfigType | null;
}

// /approvals/:tab? — tab ∈ timesheets | absences.
function ApprovalsPage({ me, badges, workflowConfig }: ApprovalsPageProps) {
  const { tab } = useParams();
  const navigate = useNavigate();
  const canApproveAbs = canApproveAbsences(me);
  // US-50: LR-этап согласования таймшитов активен только при stage='before_pm'.
  // При other-stage скрываем LR-очередь таймшитов из навигации — endpoint всё
  // равно вернёт 409. Если config ещё не подгружен — fall-back behaves like
  // before_pm, чтобы не «мигало» отключением для legacy-юзера.
  const lrTimesheetStageEnabled =
    workflowConfig === null || workflowConfig.timesheet_lr_stage === "before_pm";
  const canLR = canApproveTimesheetsAsLR(me) && lrTimesheetStageEnabled;
  const canPM = canApproveTimesheetsAsPM(me);
  const showTimesheetsSubtab = canLR || canPM;
  const showBothTimesheetQueues = canLR && canPM;
  // US-44: counts per-tab для бейджа в TabLabel.
  const tsTabCount = approvalTimesheetTabBadge(badges);
  const absTabCount = approvalAbsenceTabBadge(badges);

  // Невалидный/недоступный tab из URL → дефолтный по ролям.
  const defaultTab = showTimesheetsSubtab ? "timesheets" : "absences";
  const activeTab =
    (tab === "timesheets" && showTimesheetsSubtab) || (tab === "absences" && canApproveAbs)
      ? tab
      : defaultTab;

  return (
    <Tabs
      value={activeTab}
      onChange={(v) => navigate(`/approvals/${v ?? defaultTab}`)}
      keepMounted={false}
    >
      <Tabs.List>
        {showTimesheetsSubtab && (
          <Tabs.Tab
            value="timesheets"
            rightSection={
              tsTabCount > 0 ? (
                <Badge
                  size="xs"
                  color="red"
                  variant="filled"
                  radius="xl"
                  data-testid="approval-timesheets-tab-badge"
                >
                  {tsTabCount}
                </Badge>
              ) : null
            }
          >
            Таймшиты
          </Tabs.Tab>
        )}
        {canApproveAbs && (
          <Tabs.Tab
            value="absences"
            rightSection={
              absTabCount > 0 ? (
                <Badge
                  size="xs"
                  color="red"
                  variant="filled"
                  radius="xl"
                  data-testid="approval-absences-tab-badge"
                >
                  {absTabCount}
                </Badge>
              ) : null
            }
          >
            Отсутствия
          </Tabs.Tab>
        )}
      </Tabs.List>
      {showTimesheetsSubtab && (
        <Tabs.Panel value="timesheets" pt="md">
          <Stack gap="lg">
            {canLR && (
              <TimesheetApprovalQueue
                variant="lr"
                title={showBothTimesheetQueues ? "Мои подчинённые" : undefined}
              />
            )}
            {canPM && (
              <TimesheetApprovalQueue
                variant="pm"
                title={showBothTimesheetQueues ? "Мои проекты" : undefined}
              />
            )}
          </Stack>
        </Tabs.Panel>
      )}
      {canApproveAbs && (
        <Tabs.Panel value="absences" pt="md">
          {/* US-107: у гибрида ЛР+РП две очереди стопкой — как у таймшитов.
              Ступень РП идёт первой: она предшествует ЛР по маршруту. */}
          <Stack gap="lg">
            {canPM && <AbsenceApprovalQueue variant="pm" />}
            {canApproveAbsencesAsLRorHR(me) && <AbsenceApprovalQueue variant="lr" />}
          </Stack>
        </Tabs.Panel>
      )}
    </Tabs>
  );
}

// ---------------------------------------------------------------------------
// Reports — /reports/:tab? + drill-down через search params (US-22 / US-90)
// ---------------------------------------------------------------------------
//
// Drill-down: клик по строке в «Исключениях» ведёт на
// /reports/weekly?employee=<id>&week=<YYYY-MM-DD> — фильтры «Недельного
// свода» предзаполняются из URL (раньше — nonce-хак через локальный стейт).
// `key` по параметрам ремоунтит WeeklySummaryView при новой цели drill-down.

interface ReportsViewProps {
  me: Me;
}

const REPORT_TABS = ["weekly", "monthly", "exceptions", "zup", "reserve"] as const;

function ReportsView({ me }: ReportsViewProps) {
  const { tab } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // US-47.1 (17.07): вкладку «Выгрузка 1С ЗУП» видят обладатели выделенной
  // роли `zup_export` + admin/staff. Источник правды — backend can_export_zup.
  const canZup = canExportZup(me);
  const canReserve = canSeeReserveReport(me);
  // Своды — только управленцам. У «чистого» носителя zup_export их нет: в них
  // он увидел бы одну свою строку (report_scope = {self}) — бессмысленный шум.
  const canSummary = canSeeSummaryReports(me);
  const fallbackTab = canSummary ? "weekly" : canZup ? "zup" : "reserve";

  const requested = REPORT_TABS.includes(tab as (typeof REPORT_TABS)[number]) ? tab : undefined;
  const allowed =
    requested === "zup"
      ? canZup
      : requested === "reserve"
        ? canReserve
        : requested !== undefined
          ? canSummary
          : false;
  const tabValue = allowed ? (requested as string) : fallbackTab;

  // Drill-down параметры читаются только на табе weekly.
  const drillEmployeeId = searchParams.get("employee");
  const drillWeek = parseWeekParam(searchParams.get("week") ?? undefined);
  const drillKey = `${drillEmployeeId ?? ""}:${drillWeek?.format("YYYY-MM-DD") ?? ""}`;

  const handleDrillDown = (employeeId: number, weekStartIso: string) => {
    navigate(`/reports/weekly?employee=${employeeId}&week=${weekStartIso}`);
  };

  return (
    <Tabs
      value={tabValue}
      // Переключение таба сбрасывает drill-down параметры (чистый URL).
      onChange={(v) => navigate(`/reports/${v ?? fallbackTab}`)}
      keepMounted={false}
    >
      <Tabs.List>
        {canSummary && <Tabs.Tab value="weekly">Недельный свод</Tabs.Tab>}
        {canSummary && <Tabs.Tab value="monthly">Месячный свод</Tabs.Tab>}
        {canSummary && <Tabs.Tab value="exceptions">Исключения</Tabs.Tab>}
        {canZup && <Tabs.Tab value="zup">Выгрузка 1С ЗУП</Tabs.Tab>}
        {canReserve && <Tabs.Tab value="reserve">Резервный отчёт</Tabs.Tab>}
      </Tabs.List>
      {canSummary && (
        <>
          <Tabs.Panel value="weekly" pt="md">
            <WeeklySummaryView
              key={drillKey}
              initialWeekStart={drillWeek?.toDate()}
              initialEmployeeId={drillEmployeeId}
            />
          </Tabs.Panel>
          <Tabs.Panel value="monthly" pt="md">
            <MonthlySummaryView />
          </Tabs.Panel>
          <Tabs.Panel value="exceptions" pt="md">
            <ExceptionsReportView onDrillDown={handleDrillDown} />
          </Tabs.Panel>
        </>
      )}
      {canZup && (
        <Tabs.Panel value="zup" pt="md">
          <ZupExportView canFill={me.can_fill_zup ?? (me.is_staff || me.is_superuser)} />
        </Tabs.Panel>
      )}
      {canReserve && (
        <Tabs.Panel value="reserve" pt="md">
          <ReserveReportView />
        </Tabs.Panel>
      )}
    </Tabs>
  );
}

// ---------------------------------------------------------------------------
// App — outer shell
// ---------------------------------------------------------------------------

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  // US-90: активный раздел определяется URL-маршрутом, а не локальным стейтом —
  // F5 и deep-links сохраняют раздел.
  const location = useLocation();
  const navigate = useNavigate();
  const page = pageFromPathname(location.pathname);
  // Мобильный shell: на узких экранах сайдбар скрыт за бургером — иначе он
  // съедает половину 390px-вьюпорта. На desktop поведение не меняется.
  const [navOpened, { toggle: toggleNav, close: closeNav }] = useDisclosure(false);
  const handlePage = useCallback(
    (next: PageId) => {
      navigate(PAGE_PATHS[next]);
      closeNav(); // мобильный сайдбар закрываем после выбора раздела
    },
    [navigate, closeNav],
  );

  // US-100: command-palette ⌘K. tagsToIgnore=[] — открывается даже из инпутов
  // (например из ячейки таймшита).
  const [paletteOpen, setPaletteOpen] = useState(false);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  useHotkeys([["mod+K", () => setPaletteOpen((o) => !o)]], []);
  const paletteSections = useMemo<PaletteSection[]>(
    () =>
      me
        ? NAV_ITEMS.filter((n) => n.visible(me)).map((n) => ({
            id: n.id,
            label: n.label,
            path: PAGE_PATHS[n.id],
            icon: n.icon,
          }))
        : [],
    [me],
  );
  // US-50: workflow config (singleton). Подгружается раз после успешного
  // /api/me/, потом prop'ом стекается в PageContent. null = ещё не известно
  // (рендерим как 'before_pm' — legacy-safe).
  const [workflowConfig, setWorkflowConfig] = useState<WorkflowConfigType | null>(null);

  // US-44: счётчики уведомлений (polled каждые 30s).
  // US-59 #8: тот же hook вынесен в BadgesProvider (main.tsx). Здесь только
  // читаем через useBadges() и держим ссылку на refresh, чтобы дёрнуть
  // принудительно после login (см. useEffect ниже).
  // Объявляем ДО login-useEffect, иначе TDZ — useEffect deps читает
  // refreshBadges на каждом render, при первом render строка ещё не
  // выполнена → ReferenceError.
  const { badges, refresh: refreshBadges } = useBadges();

  // Тригер пере-fetch /api/me/ после login. Версия инкрементируется → useEffect.
  const [refetchToken, setRefetchToken] = useState(0);
  useEffect(() => {
    setMe(null);
    setError(null);
    fetchMe()
      .then((next) => {
        setMe(next);
        // Подгружаем workflow-config после успешного логина. Тихо игнорируем
        // ошибку — конфиг не критичен, fallback на 'before_pm' уже встроен.
        fetchWorkflowConfig()
          .then(setWorkflowConfig)
          .catch(() => setWorkflowConfig(null));
        // US-59 #8: бейджи могут отстать после login (hook стартовал до
        // session-cookie). Принудительно пересчитываем с актуальной сессией.
        void refreshBadges();
      })
      .catch((err: Error) => setError(err.message));
    // refreshBadges стабильна (useCallback внутри useNotificationBadges),
    // но добавляем её в deps чтобы eslint-react-hooks не ругался.
  }, [refetchToken, refreshBadges]);

  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";

  // Apply Direction C neutrals via Mantine CSS variables so AppShell.Main and
  // other Mantine surfaces follow the theme.
  useEffect(() => {
    const neutrals =
      theme.other?.neutrals?.[dark ? "dark" : "light"] ?? NEUTRALS[dark ? "dark" : "light"];
    const root = document.documentElement;
    root.style.setProperty("--mantine-color-body", neutrals.bg);
    root.style.setProperty("--mantine-color-text", neutrals.text);
    root.style.setProperty("--skedd-surface", neutrals.surface);
    root.style.setProperty("--skedd-surface-alt", neutrals.surfaceAlt);
    root.style.setProperty("--skedd-border", neutrals.border);
    root.style.setProperty("--skedd-text-muted", neutrals.textMuted);
    document.body.style.backgroundColor = neutrals.bg;
    document.body.style.color = neutrals.text;
  }, [theme, dark]);

  const impersonated = me?.is_hijacked === true;
  const headerHeight = impersonated ? 88 : 52;

  // Loading / error fallbacks render before the shell — we don't have role
  // information yet to filter the sidebar.
  //
  // Если /api/me/ упал из-за отсутствия сессии (401/403/«Учетные данные не были
  // предоставлены»), это нормальное состояние «не залогинен» — рисуем форму
  // входа. Любые другие ошибки (сеть, 5xx) показываем как Alert с retry.
  if (error) {
    const looksLikeAuth =
      /401|403|Учетные данные|Authentication credentials|Not authenticated/i.test(error);
    if (looksLikeAuth) {
      return <LoginScreen onSuccess={() => setRefetchToken((n) => n + 1)} />;
    }
    return (
      <Stack align="center" py="xl">
        <Alert color="red" title="Не удалось получить /api/me/" maw={520}>
          {error}
          <Group mt="sm">
            <Button onClick={() => setRefetchToken((n) => n + 1)} size="xs">
              Повторить
            </Button>
          </Group>
        </Alert>
      </Stack>
    );
  }
  if (!me) {
    return (
      <Stack align="center" py="xl">
        <Loader size="sm" />
        <Text c="dimmed">SKEDD загружается…</Text>
      </Stack>
    );
  }

  return (
    <>
      <CommandPalette
        opened={paletteOpen}
        onClose={closePalette}
        me={me}
        sections={paletteSections}
      />
      <AppShell
        navbar={{ width: 224, breakpoint: "sm", collapsed: { mobile: !navOpened, desktop: false } }}
        header={{ height: headerHeight }}
        padding="md"
      >
        <AppShell.Header>
          {impersonated && <ImpersonationBanner username={me.username} />}
          <Topbar
            pageTitle={PAGE_TITLES[page]}
            me={me}
            onOpenPalette={openPalette}
            burger={
              <Burger
                opened={navOpened}
                onClick={toggleNav}
                hiddenFrom="sm"
                size="sm"
                aria-label="Открыть меню"
              />
            }
          />
        </AppShell.Header>
        <AppShell.Navbar p={0}>
          <Sidebar me={me} page={page} onPage={handlePage} badges={badges} />
        </AppShell.Navbar>
        <AppShell.Main>
          {me.is_hijacked && (
            <Badge color="orange" mb="sm" data-testid="impersonated-badge">
              impersonated
            </Badge>
          )}
          <Routes>
            <Route path="/" element={<LegacyRedirect me={me} />} />
            <Route
              path="/dashboard"
              element={
                canSeeDashboard(me) ? <ManagerDashboard /> : <Navigate to="/timesheet" replace />
              }
            />
            <Route
              path="/dashboard/absences"
              element={
                canSeeDashboard(me) ? <TeamAbsencesView /> : <Navigate to="/timesheet" replace />
              }
            />
            <Route
              path="/timesheet/:weekStart?"
              element={
                // Вторая половина той же петли: слепой редирект отсюда на
                // «На согласовании» замыкал круг для тех, у кого раздела нет.
                me.timesheet_exempt ? (
                  <Navigate to={canSeeApprovals(me) ? "/approvals" : "/absences"} replace />
                ) : (
                  <TimesheetPage />
                )
              }
            />
            <Route path="/absences" element={<AbsenceList />} />
            <Route
              path="/approvals/:tab?"
              element={
                canSeeApprovals(me) ? (
                  <ApprovalsPage me={me} badges={badges} workflowConfig={workflowConfig} />
                ) : (
                  <Navigate to="/timesheet" replace />
                )
              }
            />
            <Route path="/history" element={<HistoryView me={me} />} />
            <Route
              path="/reports/:tab?"
              element={
                canSeeReports(me) ? <ReportsView me={me} /> : <Navigate to="/timesheet" replace />
              }
            />
            <Route
              path="/rates"
              element={canManageRates(me) ? <RatesView /> : <Navigate to="/timesheet" replace />}
            />
            <Route path="*" element={<Navigate to="/timesheet" replace />} />
          </Routes>
        </AppShell.Main>
      </AppShell>
    </>
  );
}
